import { Button } from "@loot-os/ui/components/button";
import { toast } from "@loot-os/ui/components/sonner";
import { Textarea } from "@loot-os/ui/components/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";
import { FactGroup } from "./Facts";

const MAX_NOTES = 4000;
/** The counter appears only once the draft is this close to the limit; below that it is noise. */
const COUNTER_FROM = MAX_NOTES - 400;

/**
 * Staff notes about one end user, shown here only and never to the shopper. Mount it keyed on
 * the saved value so a save or a refetch resets the draft; editors edit, viewers read. The save
 * button exists only while there is something to save.
 */
export function NotesSection({ endUserId, notes }: { endUserId: string; notes: string | null }) {
  const { can } = useTenantRole();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(notes ?? "");
  const save = useMutation(
    trpc.users.setNotes.mutationOptions({
      onSuccess: (result) => {
        toast.success(result.notes ? "Notes saved." : "Notes cleared.");
        void queryClient.invalidateQueries({ queryKey: trpc.users.pathKey() });
      },
    }),
  );
  const dirty = draft.trim() !== (notes ?? "");

  if (!can("editor")) {
    return (
      <FactGroup title="Notes">
        <p className="whitespace-pre-wrap text-sm">
          {notes ?? <span className="text-muted-foreground">None.</span>}
        </p>
      </FactGroup>
    );
  }

  return (
    <FactGroup
      title="Notes"
      action={
        (dirty || save.isPending) && (
          <Button
            type="button"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate({ endUserId, notes: draft.trim() || null })}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        )
      }
    >
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        maxLength={MAX_NOTES}
        rows={4}
        placeholder="Nothing noted yet."
        aria-label="Notes"
      />
      {draft.length >= COUNTER_FROM && (
        <span className="text-right text-xs text-muted-foreground tabular-nums">
          {draft.length} / {MAX_NOTES}
        </span>
      )}
    </FactGroup>
  );
}
